// packages/web/src/lib/hooks/use-sync-engine.ts
/**
 * useSyncEngine — automatic incremental cloud sync.
 *
 * 19BN Phase 3: monitors local IDB writes (putDraft/putResult/putProgress),
 * debounces 600ms, then PUTs the changed course to /sync/course/:id. On
 * failure, enqueues to IDB syncQueue for later retry. Offline detection via
 * navigator.onLine event → drain queue when network restores.
 *
 * Only active for authenticated users (anonymous = local-only, no sync).
 *
 * Spec: docs/architecture/account-system-design.md §3.3, §9.3.4
 */

import { useEffect, useRef } from 'react';
import { useStore } from '../store/index.js';
import type { ProgressSyncEvent } from '../../api/sync.js';

const SYNC_DEBOUNCE_MS = 600;
const SYNC_POLL_INTERVAL_MS = 30_000; // drain queue every 30s when online

/**
 * 19BN Phase D D6: throttle flag for the sync-failure notification. Without
 * this, a persistently-failing queue would push one warning notification per
 * 30s poll. The flag is set on the first failure and reset 5min later so a
 * genuinely new failure can notify again. Module-level (not in the hook) so
 * it survives re-renders.
 */
let syncFailureNotified = false;

export function useSyncEngine(): void {
  const currentUser = useStore((s) => s.currentUser);
  const saveStatus = useStore((s) => s.saveStatus);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Online/offline tracking → save-slice.
  useEffect(() => {
    const goOnline = () => useStore.getState().setOnline(true);
    const goOffline = () => useStore.getState().setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Main sync loop: only for authenticated + online users.
  useEffect(() => {
    const isAuthed = currentUser && !currentUser.isAnonymous;
    if (!isAuthed) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    // Periodic queue drain (catches entries from offline periods).
    const drainQueue = async () => {
      if (!useStore.getState().isOnline) return;
      try {
        const { listPendingSync, updateSyncEntry } = await import('../../storage/idb.js');
        const { syncApi } = await import('../../api/sync.js');
        const pending = await listPendingSync();
        if (pending.length === 0) return;

        useStore.getState().setPendingCount(pending.length);
        useStore.getState().startSaving();

        for (const entry of pending) {
          try {
            // 19BO+ B4.4: dispatch by entry type. 'course' → draft opaque blob;
            // 'progress' → learning-progress field-level events batch.
            if (entry.type === 'progress') {
              const { progressSyncApi } = await import('../../api/sync.js');
              await progressSyncApi.uploadEvents(entry.courseId, entry.payload as ProgressSyncEvent[]);
            } else {
              await syncApi.syncCourse(entry.courseId, entry.payload as Record<string, unknown>);
            }
            await updateSyncEntry(entry.id, { status: 'synced', attempts: entry.attempts + 1 });
          } catch {
            await updateSyncEntry(entry.id, { status: 'failed', attempts: entry.attempts + 1, lastAttemptAt: Date.now() });
          }
        }

        // Clean up synced entries.
        const { clearSyncedEntries } = await import('../../storage/idb.js');
        await clearSyncedEntries();
        useStore.getState().markSaved();
      } catch {
        useStore.getState().markError();
        // 19BN Phase D D6: surface sync failure to the message center. Uses
        // pushNotification directly (not useNotify) because this hook runs at
        // App root — calling useNotify here would couple it to the EventBus
        // provider's mount order. The save-status indicator already gives the
        // immediate toast-like feedback; this notification is the persistent
        // record. Capped via a module-level flag so a stuck queue doesn't spam
        // one notification per 30s poll.
        if (!syncFailureNotified) {
          syncFailureNotified = true;
          useStore.getState().pushNotification({
            source: 'system',
            level: 'warning',
            title: '云同步失败',
            message: '部分课程未同步到云端，将在网络恢复后重试',
            persistent: false,
          });
          // Reset the flag after 5 min so a genuinely new failure can notify again.
          setTimeout(() => { syncFailureNotified = false; }, 5 * 60 * 1000);
        }
      }
    };

    // Run drain immediately + set up interval.
    void drainQueue();
    pollRef.current = setInterval(drainQueue, SYNC_POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [currentUser]);

  // Auto-save trigger: when saveStatus is 'pending' (set by markPending),
  // debounce then drain.
  useEffect(() => {
    if (saveStatus !== 'pending') return;
    const isAuthed = currentUser && !currentUser.isAnonymous;
    if (!isAuthed) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!useStore.getState().isOnline) return;
      // Enqueue current draft to syncQueue, then drain.
      try {
        const draft = useStore.getState().draft;
        if (draft) {
          const { enqueueSync } = await import('../../storage/idb.js');
          await enqueueSync({ type: 'course', courseId: draft.id, payload: draft });
        }
      } catch {
        // non-critical
      }
      // Trigger drain by setting pending (the interval will pick it up,
      // or Ctrl+S will flush immediately).
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [saveStatus, currentUser]);
}
